import { ReactElement, useMemo } from "react";
import dayjs from "dayjs";
import { Stack, Button, Separator, Tooltip, Typography } from "@altimateai/lego";
import PrivacyAndCreatorInfo from "@/components/PrivacyAndCreatorInfo";
import UseDatamateWithComponent from "@/pages/teammate/UseDatamateWithComponent";
import { PremiumBanner } from "@/pages/teammate/dataMates/PremiumBanner";
import { Link } from "react-router-dom";
import { IntegrationName } from "@/pages/mcp/constants";
import { AltimateLogo } from "@assets/images";
import { RelativeComponentLoader } from "@/components/loader";
import { Teammate, Usage } from "@/pages/mcp/mcpSlice";
import { getIntegrationIcon } from "../utils";
import { useCanEditPrivacyAware } from "@/helpers/privacyPermissions";
import { Privacy } from "@/helpers/constants";
import { VisibilityFilter } from "./DataMatesFilters";
import { IconSettings } from "@tabler/icons-react";
import { StatMetric } from "./StatMetric";

export function getDataMateIcon(name: string): ReactElement {
  if (name === "Pipeline Manager") {
    return (
      <Stack justify="center" align="center" className="al-p-2 al-bg-blue-50 al-rounded-full">
        <i className="codicon codicon-list-selection al-text-blue-500"></i>
      </Stack>
    );
  } else if (name === "Quality Monitor") {
    return (
      <Stack justify="center" align="center" className="al-p-2 al-bg-orange-50 al-rounded-full">
        <i className="codicon codicon-shield al-text-orange-500"></i>
      </Stack>
    );
  } else if (name === "SQL Expert") {
    return (
      <Stack justify="center" align="center" className="al-p-2 al-bg-teal-50 al-rounded-full">
        <i className="codicon codicon-database al-text-teal-500"></i>
      </Stack>
    );
  } else {
    return (
      <Stack justify="center" align="center" className="al-p-2 al-bg-blue-50-50 al-rounded-full">
        <AltimateLogo className="al-h-4 al-w-4" />
      </Stack>
    );
  }
}

export interface DataMatesListProps {
  teammatesFromServer: Teammate[] | undefined;
  isLoading: boolean;
  usage: Usage | undefined;
  searchQuery?: string;
  visibility?: VisibilityFilter;
}

export function DataMatesList({
  teammatesFromServer,
  isLoading,
  usage,
  searchQuery = "",
  visibility = "all",
}: DataMatesListProps): ReactElement {
  const canEditDatamate = useCanEditPrivacyAware();

  const dataMates = useMemo(() => {
    const mapped = teammatesFromServer?.map(teamMate => ({
      id: teamMate.id,
      name: teamMate.name,
      tech: teamMate.integrations
        ?.map(integration => {
          return Object.values(IntegrationName).includes(integration.id as IntegrationName)
            ? (integration.id as IntegrationName)
            : undefined;
        })
        .filter((integration): integration is IntegrationName => integration !== undefined),
      description: teamMate.description,
      icon: getDataMateIcon(teamMate.name),
      privacy: teamMate.privacy || Privacy.PRIVATE,
      user_id: teamMate.user_id,
      created_at: teamMate.created_at,
    }));

    if (!mapped) return undefined;

    return mapped.filter(dataMate => {
      // Filter by search query (case-insensitive)
      const matchesSearch =
        searchQuery === "" || dataMate.name.toLowerCase().includes(searchQuery.toLowerCase());

      // Filter by visibility
      const matchesVisibility = visibility === "all" || dataMate.privacy === visibility;

      return matchesSearch && matchesVisibility;
    });
  }, [teammatesFromServer, searchQuery, visibility]);

  if (isLoading) {
    return <RelativeComponentLoader label="loading..." />;
  }

  // Show empty state when there are no datamates from server
  if (!teammatesFromServer || teammatesFromServer.length === 0) {
    return (
      <div className="al-rounded-2xl al-bg-white">
        <Stack
          direction="column"
          gap="lg"
          className="al-p-20 al-m-4 al-bg-white al-rounded-2xl al-text-center al-border-dotted al-border-2 al-border-gray-20"
          justify="center"
          align="center"
        >
          <span className="al-text-3xl">You don&#39;t have any datamates yet</span>
          <span>
            click
            <Button variant="link" asChild>
              <Link to="/datamates/create">here</Link>
            </Button>
            to add one
          </span>
        </Stack>
      </div>
    );
  }

  // Show no results state when filters don't match any datamates
  if (!dataMates || dataMates.length === 0) {
    return (
      <div className="al-rounded-2xl al-bg-white">
        <Stack
          direction="column"
          gap="lg"
          className="al-p-12 al-m-4 al-bg-white al-rounded-2xl al-text-center al-border-dotted al-border-2 al-border-gray-200"
          justify="center"
          align="center"
        >
          <span className="al-text-xl al-text-gray-500">No datamates match your filters</span>
          <span className="al-text-sm al-text-gray-400">
            Try adjusting your search or visibility filter
          </span>
        </Stack>
      </div>
    );
  }

  return (
    <div className="al-grid-cols-1 al-grid md:al-grid-cols-3 al-gap-4">
      {dataMates.map((dataMate, index) => {
        const originalTeammate = teammatesFromServer?.find(t => t.id === dataMate.id);
        return (
          <Link
            key={index}
            to={`/datamate/${dataMate.id}`}
            className="al-block al-no-underline al-h-full"
          >
            <Stack
              direction="column"
              className="al-bg-white al-rounded-2xl hover:al-border-action-brand al-cursor-pointer hover:al-ring-1 al-ring-action-brand al-h-full"
              gap="sm"
              role="article"
              aria-label={`${dataMate.name} datamate card`}
            >
              <Stack className="al-p-4" align="center">
                {dataMate.icon}
                <div className="al-flex-1">
                  <span className="al-text-sm">{dataMate.name}</span>
                </div>
                <Stack gap="sm" align="center">
                  {canEditDatamate(dataMate) && (
                    <span className="al-text-blue-500">
                      <Tooltip title="Edit datamate settings">
                        <Button variant="ghost" asChild>
                          <Link
                            to={`/datamate/edit/${dataMate.id}`}
                            onClick={e => e.stopPropagation()}
                            aria-label="Edit datamate settings"
                          >
                            <IconSettings size={16} />
                          </Link>
                        </Button>
                      </Tooltip>
                    </span>
                  )}
                </Stack>
              </Stack>
              {dataMate.user_id && (
                <>
                  <PrivacyAndCreatorInfo
                    privacy={dataMate.privacy || Privacy.PRIVATE}
                    userId={dataMate.user_id}
                    className="al-pr-4 al-pl-4"
                  />
                </>
              )}
              <Separator />
              <Stack className="al-p-4 al-flex-1" direction="column" gap="2xl">
                <Stack gap="md">
                  {dataMate.tech?.map((techName, index) => (
                    <div key={index} className="al-h-4 al-w-4">
                      {getIntegrationIcon(techName)}
                    </div>
                  ))}
                </Stack>
                <div>
                  <Typography
                    variant="caption"
                    className="al-text-gray-500 al-overflow-hidden al-max-h-8"
                  >
                    {dataMate.description}
                  </Typography>
                </div>
              </Stack>
              <Stack gap="lg" justify="around" className="al-p-4">
                {dataMate?.id && (
                  <StatMetric
                    label="Assists"
                    value={usage?.datamates_usage[dataMate.id]?.assists ?? 0}
                    id={`assists-${dataMate.id}`}
                  />
                )}
                {dataMate?.id && (
                  <StatMetric
                    label="Guardrails"
                    value={usage?.datamates_usage[dataMate.id]?.guardrails ?? 0}
                    id={`guardrails-${dataMate.id}`}
                  />
                )}
                <Stack gap="md" direction="column" className="al-flex-1">
                  <div className="al-max-h-[80px]">
                    <PremiumBanner variant={"small"} />
                  </div>
                  <Typography variant="caption" className="al-text-gray-500">
                    LLM Costs
                  </Typography>
                </Stack>
              </Stack>
              <Separator />
              <Stack className="al-p-4" align="center">
                <div className="al-flex-1">
                  <UseDatamateWithComponent teammate={originalTeammate} />
                </div>
                <Stack justify="end" className="al-flex-1 al-text-gray-500">
                  Created:{" "}
                  {dataMate.created_at ? dayjs(dataMate.created_at).format("MMM D, YYYY") : "N/A"}
                </Stack>
              </Stack>
            </Stack>
          </Link>
        );
      })}
    </div>
  );
}
